<template>
  <div>
    <slot :set-ref="setRef" />
    <div
      v-if="(showDots || showArrows) && isAllowed && isScrollable"
      :class="[controlWrapperClasses, 'flex flex-col', {
        'mt-6': showDots && !showArrows,
        'mt-12': showArrows && !showDots,
      }]"
    >
      <ul
        v-if="showDots && isAllowed"
        class="flex items-center justify-center gap-2"
      >
        <li
          v-for="i in numberOfItems"
          :key="`slider-dot-${i}`"
        >
          <button
            type="button"
            :class="{
              'bg-neutral-200 hover:opacity-50': !isDark && validCurrentIndex !== i - 1,
              'bg-ocean-900': !isDark && validCurrentIndex === i - 1,
              'bg-neutral-500 hover:opacity-50': isDark && validCurrentIndex !== i - 1,
              'bg-white': isDark && validCurrentIndex === i - 1,
            }"
            class="size-2.5 rounded-full"
            @click="scrollToIndex(i - 1)"
          />
        </li>
      </ul>
      <div
        v-if="showArrows && isAllowed"
        class="flex justify-center items-center gap-8"
      >
        <button
          ref="prevButton"
          type="button"
          :disabled="!hasPrev"
          :aria-label="$t('app.previous')"
          :class="{ 'cursor-not-allowed opacity-50': !hasPrev }"
          class="duration-300 ease-in-out transition-opacity"
          @click.stop.prevent="scrollTo('left')"
        >
          <img
            :src="getAssetUrl(arrowImage)"
            :alt="$t('app.previous')"
            class="rotate-180 -mt-px"
          />
        </button>
        <button
          ref="nextButton"
          type="button"
          :disabled="!hasNext"
          :aria-label="$t('app.next')"
          :class="{ 'cursor-not-allowed opacity-50': !hasNext }"
          class="duration-300 ease-in-out transition-opacity"
          @click.stop.prevent="scrollTo('right')"
        >
          <img
            :src="getAssetUrl(arrowImage)"
            :alt="$t('app.next')"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getAssetUrl } from '@/helpers/url';

const Device = {
  Mobile: 'mobile',
  Tablet: 'tablet',
  Desktop: 'desktop',
};

const ArrowsColor = {
  Bubblegum: 'bubblegum',
  Ocean: 'ocean',
};

export default {
  name: 'VetsterSimpleSlider',

  props: {
    allowOn: {
      type: Array,
      default: () => ([
        Device.Mobile,
        Device.Tablet,
        Device.Desktop,
      ]),
    },
    arrows: {
      type: [Boolean, Array],
      default: false,
    },
    arrowsColor: {
      type: String,
      default: ArrowsColor.Bubblegum,
    },
    centerOnScroll: {
      type: Boolean,
      default: false,
    },
    controlWrapperClasses: {
      type: String,
      default: '',
    },
    dots: {
      type: [Boolean, Array],
      default: false,
    },
    isDark: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      canScroll: true,
      currentIndex: 0,
      focusedElement: null,
      hasNext: true,
      hasPrev: true,
      isDragging: false,
      numberOfItems: 0,
      observer: null,
      scrollStart: 0,
      slotRefs: {
        container: null,
        list: null,
      },
      startX: 0,
    };
  },

  computed: {
    arrowImage() {
      return this.arrowsColor === ArrowsColor.Bubblegum
        ? 'images/arrows/straight-shortest-pink-arrow.svg'
        : 'images/arrows/straight-shortest-current-arrow.svg';
    },
    devices() {
      return [
        Device.Desktop,
        Device.Tablet,
        Device.Mobile,
      ];
    },
    isAllowed() {
      return this.devices.some((device) => this.$screen(device) && this.allowOn.includes(device));
    },
    isScrollable() {
      return this.hasPrev || this.hasNext;
    },
    showArrows() {
      return this.arrows === true || this.devices.some((device) => this.$screen(device) && this.arrows?.includes?.(device));
    },
    showDots() {
      return this.dots === true || this.devices.some((device) => this.$screen(device) && this.dots?.includes?.(device));
    },
    validCurrentIndex() {
      return Math.max(0, Math.min(this.currentIndex, this.numberOfItems - 1));
    },
  },

  watch: {
    isAllowed: {
      handler(newVal) {
        if (newVal) {
          this.initSlider();
        } else {
          this.destroySlider();
        }
      },
    },
    isScrollable(newVal) {
      if (newVal) {
        this.initSlider();
      } else {
        this.destroySlider();
      }
    },
  },

  mounted() {
    this.initSlider();
  },

  beforeDestroy() {
    this.removeListeners();
  },

  methods: {
    addListeners() {
      if (!this.isAllowed) {
        return;
      }

      if ('ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0) {
        this.slotRefs.list.addEventListener('touchstart', this.onTouchStart, { passive: true });
      } else {
        this.hideScrollbar();
        this.slotRefs.list.addEventListener('pointercancel', this.onPointerCancel);
        this.slotRefs.list.addEventListener('pointerdown', this.onPointerDown);
        this.slotRefs.list.addEventListener('pointermove', this.onPointerMove);
        this.slotRefs.list.addEventListener('pointerup', this.onPointerUp);
      }

      document.addEventListener('click', this.onClick);
      window.addEventListener('keydown', this.onKeyDown);
      this.slotRefs.list.addEventListener('scroll', this.onScroll);
      this.onScroll(); // Initial call to set the current index based on initial scroll position

      if (!this.centerOnScroll) {
        this.initIntersectionObserver();
      }
    },

    destroyIntersectionObserver() {
      this.observer?.disconnect();
    },

    destroySlider() {
      this.removeListeners();
      this.slotRefs.list.classList.remove('whitespace-nowrap', 'overflow-x-auto');
    },

    findClosestItemTo(position) {
      const items = Array.from(this.slotRefs.list.children);

      if (items.length === 0) {
        return null;
      }

      let closestItem = null;
      let closestDistance = Infinity;
      const centerPosition = position;

      items.forEach((item) => {
        const itemCenter = item.offsetLeft + (item.offsetWidth / 2);
        const distance = Math.abs(itemCenter - centerPosition);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestItem = item;
        }
      });

      return closestItem;
    },

    getAssetUrl,

    hideScrollbar() {
      this.slotRefs.list.style.scrollbarWidth = 'none';
      this.slotRefs.list.style.msOverflowStyle = 'none';
    },

    initIntersectionObserver() {
      // Legacy Safari iOS browser.
      if (!window.IntersectionObserver) {
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.canScroll) {
            const items = Array.from(this.slotRefs.list.children);
            this.currentIndex = items.indexOf(entry.target) - 1;
          }
        });
      }, {
        root: this.slotRefs.list,
        rootMargin: '0px',
        threshold: 0.5, // Trigger when 50% of the item is visible
      });

      Array.from(this.slotRefs.list.children).forEach((item) => this.observer.observe(item));
    },

    initSlider() {
      this.$nextTick(() => {
        this.addListeners();
        this.slotRefs.list.classList.add('whitespace-nowrap', 'overflow-x-auto');
        this.numberOfItems = this.slotRefs.list.children.length;

        if (this.centerOnScroll && (this.$screen(Device.Mobile) || this.$screen(Device.Tablet))) {
          this.scrollTo('right');
        }
      });
    },

    onClick(e) {
      // Onclick outside the list, remove focus
      if (!this.slotRefs.container.contains(e.target)) {
        this.focusedElement = null;
      } else {
        document.activeElement.blur();
      }
    },

    onKeyDown(e) {
      if (!this.slotRefs.container.contains(this.focusedElement)) {
        return;
      }

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();

          if (this.hasNext) {
            this.$refs.nextButton?.focus();
            this.scrollTo('right');
          }

          break;
        case 'ArrowLeft':
          e.preventDefault();

          if (this.hasPrev) {
            this.$refs.prevButton?.focus();
            this.scrollTo('left');
          }

          break;
        default:
          break;
      }
    },

    onPointerCancel(e) {
      if (this.isDragging) {
        this.isDragging = false;
        this.slotRefs.list.releasePointerCapture(e.pointerId);
      }
    },

    onPointerDown(e) {
      if (!this.slotRefs.list.contains(e.target)) {
        return;
      }

      e.preventDefault();

      this.isDragging = true;
      this.startX = e.clientX;
      this.scrollStart = this.slotRefs.list.scrollLeft;
      this.focusedElement = e.target;
    },

    onPointerMove(e) {
      if (!this.slotRefs.list.contains(e.target) || !this.isDragging || e.buttons !== 1) {
        return;
      }

      this.slotRefs.list.setPointerCapture(e.pointerId);
      this.slotRefs.list.scrollLeft = this.scrollStart - (e.clientX - this.startX);
    },

    onPointerUp(e) {
      if (!this.slotRefs.list.contains(e.target) || !this.isDragging || this.startX === e.clientX) {
        return;
      }

      this.isDragging = false;
      this.slotRefs.list.releasePointerCapture(e.pointerId);
    },

    onScroll() {
      if (!this.canScroll) {
        return;
      }

      const closestItem = this.findClosestItemTo(this.slotRefs.list.scrollLeft + this.slotRefs.list.clientWidth / 2);

      if (closestItem) {
        const items = Array.from(this.slotRefs.list.children);
        const i = items.indexOf(closestItem);

        if (i !== -1) {
          this.currentIndex = i;
          this.setButtonsDisabled();
        }
      }
    },

    onTouchStart(e) {
      if (!this.slotRefs.list.contains(e.target)) {
        return;
      }

      this.focusedElement = e.target;
    },

    releaseScroll() {
      this.setButtonsDisabled();
      this.canScroll = true;
      this.slotRefs.list.removeEventListener('scrollend', this.releaseScroll);
    },

    removeListeners() {
      document.removeEventListener('click', this.onClick);
      window.removeEventListener('keydown', this.onKeyDown);
      this.slotRefs.list.removeEventListener('pointercancel', this.onPointerCancel);
      this.slotRefs.list.removeEventListener('pointerdown', this.onPointerDown);
      this.slotRefs.list.removeEventListener('pointermove', this.onPointerMove);
      this.slotRefs.list.removeEventListener('pointerup', this.onPointerUp);
      this.slotRefs.list.removeEventListener('scroll', this.onScroll);
      this.slotRefs.list.removeEventListener('touchstart', this.onTouchStart);
      this.destroyIntersectionObserver();
    },

    scrollTo(direction) {
      const items = Array.from(this.slotRefs.list.children);

      if (items.length === 0 || !this.canScroll) {
        return;
      }

      const nextIndex = (direction === 'right' ? this.currentIndex + 1 : this.currentIndex - 1);
      const targetIndex = Math.max(0, Math.min(nextIndex, items.length - 1));
      const targetItem = items[targetIndex];

      if (!targetItem) {
        return;
      }

      let newScroll;

      if (this.centerOnScroll) {
        const containerWidth = this.slotRefs.list.clientWidth;
        const itemLeft = targetItem.offsetLeft; // offsetLeft is relative to the offsetParent (this.slotRefs.list)
        const itemWidth = targetItem.offsetWidth;
        newScroll = itemLeft - (containerWidth / 2) + (itemWidth / 2);
      } else {
        // Original offset-based scrolling logic
        const currentItemForOffset = items[this.currentIndex];
        const gap = parseFloat(getComputedStyle(this.slotRefs.list).gap) || 0;

        if (direction === 'right') {
          const scrollAmount = currentItemForOffset ? currentItemForOffset.offsetWidth : (targetItem.offsetWidth || 0);
          newScroll = this.slotRefs.list.scrollLeft + scrollAmount + gap;
        } else { // direction === 'left'
          const scrollAmount = targetItem.offsetWidth || 0;
          newScroll = this.slotRefs.list.scrollLeft - (scrollAmount + gap);
        }
      }

      const maxScroll = this.slotRefs.list.scrollWidth - this.slotRefs.list.clientWidth;
      newScroll = Math.max(0, Math.min(newScroll, maxScroll < 0 ? 0 : maxScroll));

      this.canScroll = false;
      this.focusedElement = targetItem;
      this.currentIndex = targetIndex;
      this.slotRefs.list.addEventListener('scrollend', this.releaseScroll);
      this.slotRefs.list.scrollTo({
        left: newScroll,
        behavior: 'smooth',
      });
    },

    scrollToIndex(index) {
      const items = Array.from(this.slotRefs.list.children);

      if (index < 0 || index >= items.length) {
        return;
      }

      this.currentIndex = index;
      this.focusedElement = items[index];

      let newScroll;

      if (this.centerOnScroll) {
        const containerWidth = this.slotRefs.list.clientWidth;
        const itemLeft = items[index].offsetLeft; // offsetLeft is relative to the offsetParent (this.slotRefs.list)
        const itemWidth = items[index].offsetWidth;
        newScroll = itemLeft - (containerWidth / 2) + (itemWidth / 2);
      } else {
        newScroll = items[index].offsetLeft;
      }

      this.canScroll = false;
      this.slotRefs.list.addEventListener('scrollend', this.releaseScroll);
      this.slotRefs.list.scrollTo({
        left: newScroll,
        behavior: 'smooth',
      });
    },

    setButtonsDisabled() {
      if (this.$refs.nextButton) {
        this.hasNext = this.slotRefs.list.scrollLeft < this.slotRefs.list.scrollWidth - this.slotRefs.list.clientWidth;
      }

      if (this.$refs.prevButton) {
        this.hasPrev = this.slotRefs.list.scrollLeft > 0 && this.currentIndex > 0;
      }
    },

    setRef(name, el) {
      this.slotRefs[name] = el;
    },
  },
};
</script>
